import type { GraphElementDefinition } from './types';

const GEO_PROPERTIES = ['country', 'countryCode', 'city', 'region', 'coordinates'] as const;

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

function resolveCountryName(code: string): string {
  try {
    return regionNames.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

function slugify(str: string): string {
  return str.toLowerCase().replace(/\s+/g, '-');
}

/**
 * Scans graph elements for nodes with country/city properties and materializes
 * them as hierarchical geolocation nodes: country → city, with the source node
 * connected via `located_in` to the most specific geolocation available.
 *
 * Runs after absorbAlerts and before normalizeEdgeDirections in the chain.
 */
export function injectGeolocationNodes(
  elements: GraphElementDefinition[],
): GraphElementDefinition[] {
  const nodes = elements.filter(e => e.group === 'nodes');
  const edges = elements.filter(e => e.group === 'edges');

  const hasCountryData = nodes.some(n => {
    const d = n.data;
    return typeof d.country === 'string' || typeof d.countryCode === 'string';
  });
  if (!hasCountryData) return elements;

  const existingGeoNodes = nodes.filter(n => n.data.type === 'geolocation');
  const existingGeoIds = new Set(existingGeoNodes.map(n => n.data.id as string));
  const existingGeoLabels = new Set(existingGeoNodes.map(n => (n.data.label as string).toLowerCase()));
  const nodesWithLocatedIn = new Set(
    edges.filter(e => e.data.label === 'located in' || e.data.label === 'contains').map(e => e.data.source as string),
  );

  const newGeoNodes = new Map<string, GraphElementDefinition>();
  const newEdges: GraphElementDefinition[] = [];
  const modifiedNodes: GraphElementDefinition[] = [];

  for (const node of nodes) {
    const d = node.data;
    const countryCode = (d.country ?? d.countryCode) as string | undefined;

    if (typeof countryCode !== 'string' || countryCode.length !== 2) {
      modifiedNodes.push(node);
      continue;
    }

    if (nodesWithLocatedIn.has(d.id as string)) {
      modifiedNodes.push(node);
      continue;
    }

    const city = typeof d.city === 'string' ? d.city : undefined;
    const code = countryCode.toUpperCase();
    const countryName = resolveCountryName(code);

    // Always create a country node (skip if one with same label already exists)
    const countryId = `geo-${code}`;
    if (!existingGeoIds.has(countryId) && !newGeoNodes.has(countryId) && !existingGeoLabels.has(countryName.toLowerCase())) {
      newGeoNodes.set(countryId, {
        group: 'nodes',
        data: { id: countryId, type: 'geolocation', label: countryName },
      });
    }

    // If city is available, create a city node and country→city contains edge
    let targetGeoId = countryId;
    if (city) {
      const cityId = `geo-${code}-${slugify(city)}`;
      targetGeoId = cityId;
      if (!existingGeoIds.has(cityId) && !newGeoNodes.has(cityId) && !existingGeoLabels.has(city.toLowerCase())) {
        newGeoNodes.set(cityId, {
          group: 'nodes',
          data: { id: cityId, type: 'geolocation', label: city },
        });
      }
      // country → city (contains)
      const containsId = `${countryId}-contains-${cityId}`;
      newEdges.push({
        group: 'edges',
        data: { id: containsId, source: countryId, target: cityId, label: 'contains' },
      });
    }

    // Source node → most specific geolocation (located_in)
    const edgeId = `${d.id}-located_in-${targetGeoId}`;
    newEdges.push({
      group: 'edges',
      data: { id: edgeId, source: d.id as string, target: targetGeoId, label: 'located in' },
    });

    // Remove geo properties from the node
    const cleaned = { ...d };
    for (const prop of GEO_PROPERTIES) {
      delete cleaned[prop];
    }
    modifiedNodes.push({ ...node, data: cleaned });
  }

  return [...modifiedNodes, ...Array.from(newGeoNodes.values()), ...edges, ...newEdges];
}

/**
 * Same as injectGeolocationNodes but operates on raw GraphNode/GraphEdge arrays
 * (the format used by the AI orchestrator backend).
 */
export function injectGeolocationIntoGraphData(data: {
  nodes: Array<{ id: string; type: string; label: string; severity?: string; properties?: Record<string, unknown> }>;
  edges: Array<{ source: string; target: string; type: string; label?: string; directed?: boolean; style?: string }>;
}): {
  nodes: Array<{ id: string; type: string; label: string; severity?: string; properties?: Record<string, unknown> }>;
  edges: Array<{ source: string; target: string; type: string; label?: string; directed?: boolean; style?: string }>;
} {
  const { nodes, edges } = data;

  const hasCountryData = nodes.some(n =>
    typeof n.properties?.country === 'string' || typeof n.properties?.countryCode === 'string',
  );
  if (!hasCountryData) return data;

  const existingGeoNodes = nodes.filter(n => n.type === 'geolocation');
  const existingGeoIds = new Set(existingGeoNodes.map(n => n.id));
  const existingGeoLabels = new Set(existingGeoNodes.map(n => n.label.toLowerCase()));
  const nodesWithLocatedIn = new Set(
    edges.filter(e => e.type === 'located_in' || e.type === 'contains').map(e => e.source),
  );

  const newGeoNodes = new Map<string, typeof nodes[number]>();
  const newEdges: typeof edges[number][] = [];
  const modifiedNodes: typeof nodes[number][] = [];

  for (const node of nodes) {
    const props = node.properties ?? {};
    const countryCode = (props.country ?? props.countryCode) as string | undefined;

    if (typeof countryCode !== 'string' || countryCode.length !== 2) {
      modifiedNodes.push(node);
      continue;
    }

    if (nodesWithLocatedIn.has(node.id)) {
      modifiedNodes.push(node);
      continue;
    }

    const city = typeof props.city === 'string' ? props.city : undefined;
    const code = countryCode.toUpperCase();
    const countryName = resolveCountryName(code);

    const countryId = `geo-${code}`;
    if (!existingGeoIds.has(countryId) && !newGeoNodes.has(countryId) && !existingGeoLabels.has(countryName.toLowerCase())) {
      newGeoNodes.set(countryId, { id: countryId, type: 'geolocation', label: countryName });
    }

    let targetGeoId = countryId;
    if (city) {
      const cityId = `geo-${code}-${slugify(city)}`;
      targetGeoId = cityId;
      if (!existingGeoIds.has(cityId) && !newGeoNodes.has(cityId) && !existingGeoLabels.has(city.toLowerCase())) {
        newGeoNodes.set(cityId, { id: cityId, type: 'geolocation', label: city });
      }
      newEdges.push({
        source: countryId, target: cityId, type: 'contains', label: 'contains',
      });
    }

    newEdges.push({
      source: node.id, target: targetGeoId, type: 'located_in', label: 'located in', style: 'dashed',
    });

    const cleaned = { ...props };
    for (const prop of GEO_PROPERTIES) {
      delete cleaned[prop];
    }
    modifiedNodes.push({ ...node, properties: Object.keys(cleaned).length > 0 ? cleaned : undefined });
  }

  return {
    nodes: [...modifiedNodes, ...Array.from(newGeoNodes.values())],
    edges: [...edges, ...newEdges],
  };
}
